import 'assets/index.css';

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom';

import { arcadeRoutes } from 'arcade/ArcadeApp';
import { ScrollToTop } from 'component-lib/atoms/ScrollToTop';
import { NotificationProvider } from 'core/context/Notifications';
import { coreAppRoutes } from 'core/CoreApp';
import { FeatureFlagManager } from 'core/molecules/feature-flag-manager/FeatureFlagManager';
import { createAppRoutes } from 'create/CreateApp';
import { playAppRoutes } from 'play/PlayApp';
import { SkilljarContextProvider } from 'play/support/skilljar';
import { redirectRoutes } from 'redirects';
import { AuthTokenContextProvider } from 'support/auth/AuthTokenContext';
import { AuthUserContextProvider, useAuthUserContext } from 'support/auth/AuthUserContext';
import { GraphQLProvider } from 'support/GraphQLProvider';
import { trackAnonUser, trackPageView, trackUser } from 'support/tracking';

const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)([
  {
    path: '*',
    element: (
      <NotificationProvider>
        <AuthTokenContextProvider>
          <GraphQLProvider>
            <AuthUserContextProvider>
              <SkilljarContextProvider>
                <RouterEntry />
              </SkilljarContextProvider>
            </AuthUserContextProvider>
          </GraphQLProvider>
        </AuthTokenContextProvider>
      </NotificationProvider>
    ),
    children: [
      ...redirectRoutes,
      ...arcadeRoutes,
      ...createAppRoutes,
      ...coreAppRoutes,
      ...playAppRoutes,
      { path: '*', element: <Navigate replace to="/not-found" /> },
    ],
  },
]);

export function App() {
  return (
    <Sentry.ErrorBoundary showDialog>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  );
}

function RouterEntry() {
  const { instruqtUser, isAnonymous } = useAuthUserContext();
  const location = useLocation();

  // Identify the current user in Sentry / Hubspot
  useEffect(() => {
    if (!isAnonymous) {
      trackUser(instruqtUser);
    } else {
      trackAnonUser();
    }
  }, [instruqtUser, isAnonymous]);

  // Track page views in Firebase / Google Analytics / Hubspot
  useEffect(() => {
    trackPageView(instruqtUser, location);
  }, [instruqtUser, location]);

  return (
    <>
      <div className="flex flex-col min-h-screen w-full">
        <Outlet />
      </div>

      <ScrollToTop />
      <FeatureFlagManager />
    </>
  );
}
